/**
 * Pure tree arithmetic over a flat parent-chain list.
 *
 * Its own module, with NO imports, for the same reason `pages/settingsLinks.ts`
 * is: the component that uses this pulls in react-dnd, which is ESM-only and
 * unresolvable under CRA's jest — so anything living beside it is untestable.
 * A rule with no test is a rule that quietly stops being true.
 */

/**
 * Every id beneath `nodeId`, exclusive of itself.
 *
 * The move-to picker needs this so a node cannot be dropped inside its own
 * subtree — which would detach that whole branch from the root and, since the
 * parent chain is the only structure there is, silently orphan everything under
 * it. Nothing on screen would say so.
 *
 * Breadth-first over the flat list. A node is only ever enqueued once, so a
 * cycle in the data terminates instead of hanging the tab — the parent chain is
 * a plain column and a bad backfill could write one.
 */
export function collectDescendantIds<T>(
  items: T[],
  nodeId: number,
  getId: (item: T) => number,
  getParentId: (item: T) => number | null | undefined,
): Set<number> {
  const descendants = new Set<number>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.pop()!;
    for (const item of items) {
      // null, undefined and 0 all mean "top level" — the data uses null, the
      // tree library uses 0. Normalized here so callers pass either.
      const parent = getParentId(item) ?? 0;
      const id = getId(item);
      if (parent === current && !descendants.has(id)) {
        descendants.add(id);
        queue.push(id);
      }
    }
  }
  return descendants;
}
